import { useIntl, defineMessages } from 'react-intl';
import { resolveImageURL } from 'volto-essvoltocomponents/helpers';
import {
  ContentWrapper,
  PageSection,
  ContentNarrow,
  ContentWide,
} from 'volto-essvoltocomponents/components/Layout';
import PageHeaderBackground from '../../Molecules/PageHeaderBackground';
import RichText from '../../Atoms/RichText';
import ResponsiveComponentSwitch from '../../Atoms/ResponsiveComponentSwitch';
import ResponsiveGrid from '../../Atoms/ResponsiveGrid';
import Carousel from '../../Atoms/Carousel';
import TeaserCardVertical from '../../Molecules/TeaserCard/TeaserCardVertical';
import TeaserCardHorizontal from '../../Molecules/TeaserCard/TeaserCardHorizontal';
import { validateType } from '../../../helpers/contentValidators';
import { ESSButton } from '../../Atoms/ESSButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GRID_BREAKPOINTS = [
  { minWidth: 0, columns: 1 },
  { minWidth: 768, columns: 2 },
  { minWidth: 1440, columns: 4 },
];

const PAGE_SECTIONS = [
  { id: 'section-menu', title: 'Menu' },
  { id: 'section-about', title: 'About the sector' },
  { id: 'section-our-work', title: 'What we do' },
  { id: 'section-relevance', title: 'Related projects & services' },
  { id: 'section-success-story', title: 'Success Story' },
  { id: 'section-resources', title: 'Related resources' },
];

const messages = defineMessages({
  resourcesExploreMore: {
    id: 'resources.exploreMore',
    defaultMessage: 'VIEW ALL',
  },
  relevanceExploreMore: {
    id: 'relevance.exploreMore',
    defaultMessage: 'VIEW ALL',
  },
});

const EMPTY_ARRAY = [];

// ---------------------------------------------------------------------------
// SectorView
// ---------------------------------------------------------------------------

const SectorView = ({ content }) => {
  const intl = useIntl();
  const title = content.title;
  const titleBgImageUrl = resolveImageURL(content, '/@@images/hero_image/hero');
  const fullDescription = content.full_description?.data;
  const about = content.about?.data;
  const ourWork = content.what_we_do?.data;

  // "success_story" relationchoice validation
  const successStory = content?.success_story; // single object or null
  const isValidSuccessStory = validateType(successStory, ['success_story']);

  // "work" relationlist validation
  const relatedWorkList = content?.work || EMPTY_ARRAY;
  const relatedWorkItems = relatedWorkList
    .filter((item) => validateType(item, ['project', 'sector', 'service']))
    .slice(0, 4);
  const relatedWorkHasItems = relatedWorkItems.length > 0;

  // "resources" relationlist validation
  const relatedResourcesList = content?.resources || EMPTY_ARRAY;
  const relatedResourcesItems = relatedResourcesList
    .filter((item) =>
      validateType(item, ['resource', 'resource_list', 'success_story']),
    )
    .slice(0, 4);
  const relatedResourcesHasItems = relatedResourcesItems.length > 0;

  // related_work_items Card renderer (identical to Resources_items
  const relatedWorkCardElements = relatedWorkItems.map((item, index) => {
    return (
      <TeaserCardVertical
        key={item['@id'] || index}
        title={item.title || ''}
        description={item?.short_description?.data || ''}
        imageUrl={resolveImageURL(item, '/@@images/thumbnail_image/preview')}
        url={item['@id'] || ''}
      />
    );
  });

  // relatedResourcesItems Card renderer (identical to Resources_items)
  const relatedResourcesCardElements = relatedResourcesItems.map(
    (item, index) => {
      return (
        <TeaserCardVertical
          key={item['@id'] || index}
          title={item.title || ''}
          description={item?.short_description?.data || ''}
          imageUrl={resolveImageURL(item, '/@@images/thumbnail_image/preview')}
          url={item['@id'] || ''}
        />
      );
    },
  );

  const availableSections = PAGE_SECTIONS.filter((section, index) => {
    switch (section.id) {
      case 'section-about':
        return !!about;
      case 'section-our-work':
        return !!ourWork;
      case 'section-success-story':
        return !!isValidSuccessStory;
      case 'section-resources':
        return relatedResourcesHasItems;
      case 'section-relevance':
        return relatedWorkHasItems;
      default:
        return false;
    }
  });

  const borderStyle = 'solid 1px var(--color-neutral-01)';

  return (
    <>
      <PageHeaderBackground
        title={title}
        description={fullDescription}
        showBackButton={true}
        goBackText="Go back"
        backgroundImage={titleBgImageUrl}
        blurBg={true}
      />
      {/* Menu */}
      <PageSection
        id={availableSections[0].id}
        paddingTop="var(--spacing-3)"
        paddingBottom="var(--spacing-3)"
        borderTop={borderStyle}
      >
        <ContentWrapper className="sector-menu-wrapper">
          <ContentWide
            className="sector-menu"
            style={{ flexDirection: 'row', flexWrap: 'wrap' }}
          >
            {availableSections.map((section) => (
              <ESSButton
                variant="link-icon"
                key={section.id}
                onClick={() =>
                  document.getElementById(section.id)?.scrollIntoView({
                    behavior: 'smooth',
                  })
                }
              >
                {section.title}
              </ESSButton>
            ))}
          </ContentWide>
        </ContentWrapper>
      </PageSection>

      {/* About */}
      {about && (
        <PageSection id={PAGE_SECTIONS[1].id} borderTop={borderStyle}>
          <ContentWrapper className="sector-about-wrapper">
            <ContentWide>
              <ContentNarrow className="sector-about-title">
                <h2>{PAGE_SECTIONS[1].title}</h2>
              </ContentNarrow>
              <ContentNarrow className="sector-about-content" alignment="left">
                <RichText content={about} />
              </ContentNarrow>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      {/* Our work */}
      {ourWork && (
        <PageSection id={PAGE_SECTIONS[2].id} borderTop={borderStyle}>
          <ContentWrapper className="sector-our-work-wrapper">
            <ContentWide>
              <ContentNarrow className="sector-our-work-title">
                <h2>{PAGE_SECTIONS[2].title}</h2>
                <p>Discover the projects and services that support our work.</p>
              </ContentNarrow>
              <ContentNarrow
                className="sector-our-work-content"
                alignment="left"
              >
                <RichText content={ourWork} />
              </ContentNarrow>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      {/* Related projects and sectors */}
      {relatedWorkHasItems && (
        <PageSection id={PAGE_SECTIONS[3].id} borderTop={borderStyle}>
          <ContentWrapper>
            <ContentWide className="sector-our-work__grid">
              <h2>{PAGE_SECTIONS[3].title}</h2>
              {/* Responsive grid: carousel on mobile, grid on desktop */}
              <ResponsiveComponentSwitch
                breakpoints={[
                  {
                    minWidth: 768,
                    component: (
                      <ResponsiveGrid breakpoints={GRID_BREAKPOINTS}>
                        {relatedWorkCardElements}
                      </ResponsiveGrid>
                    ),
                  },
                ]}
                defaultComponent={
                  <Carousel>{relatedWorkCardElements}</Carousel>
                }
              />
            </ContentWide>
            <ContentWide
              className="sector-our-work__cta-link"
              alignment="center"
            >
              <ESSButton
                variant="link-icon"
                className="sector-our-work__explore-link"
                href="/our-work?section=projects"
              >
                {intl.formatMessage(messages.relevanceExploreMore)}
                <FontAwesomeIcon icon={faChevronRight} size="sm" />
              </ESSButton>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      {/* Success story*/}
      {isValidSuccessStory && (
        <PageSection id={PAGE_SECTIONS[4].id} borderTop={borderStyle}>
          <ContentWrapper>
            <ContentWide>
              <ContentNarrow className="sector-success-story-title">
                <h2>Success Story</h2>
                Explore the projects and services related to this sector and its
                areas of application.
                <TeaserCardHorizontal
                  key={successStory['@id']}
                  title={successStory.title || ''}
                  description={successStory?.short_description?.data || ''}
                  imageUrl={resolveImageURL(
                    successStory,
                    '/@@images/thumbnail_image/preview',
                  )}
                  url={successStory['@id'] || ''}
                />
              </ContentNarrow>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      {/* Related resources */}
      {relatedResourcesHasItems && (
        <PageSection id={PAGE_SECTIONS[5].id} borderTop={borderStyle}>
          <ContentWrapper>
            <ContentWide className="sector-resources__grid">
              <h2>{PAGE_SECTIONS[5].title}</h2>
              <p>Discover the resources that support our work.</p>
              {/* Responsive grid: carousel on mobile, grid on desktop */}
              <ResponsiveComponentSwitch
                breakpoints={[
                  {
                    minWidth: 768,
                    component: (
                      <ResponsiveGrid breakpoints={GRID_BREAKPOINTS}>
                        {relatedResourcesCardElements}
                      </ResponsiveGrid>
                    ),
                  },
                ]}
                defaultComponent={
                  <Carousel>{relatedResourcesCardElements}</Carousel>
                }
              />
            </ContentWide>
            <ContentWide
              className="sector-resources__cta-link"
              alignment="center"
            >
              <ESSButton
                variant="link-icon"
                className="sector-esources__explore-link"
                href="/resources/dissemination"
              >
                {intl.formatMessage(messages.resourcesExploreMore)}
                <FontAwesomeIcon icon={faChevronRight} size="sm" />
              </ESSButton>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}
      <PageSection paddingTop="var(--ess-space-7xl)" paddingBottom="0" />
    </>
  );
};

export default SectorView;
